import type { EventDto } from 'api/generated';
import { Day } from 'lib/hooks/use-calendar';
import { getDateParts } from './date-helpers';

/**
 * Group events into calendar-day buckets (keyed by day-of-month) for the
 * viewed month, evaluating each event's day in the venue timezone. Events
 * outside the viewed month are dropped.
 */
export function groupEventsByServerDay(events: EventDto[], viewYear: number, viewMonth: number, serverTimezone: string): Record<number, EventDto[]> {
  return events.reduce<Record<number, EventDto[]>>((acc, event) => {
    const {
      year,
      month,
      day
    } = getDateParts(event.startDate ?? 0, serverTimezone);
    if (year !== viewYear || month !== viewMonth) return acc;
    (acc[day] ??= []).push(event);
    return acc;
  }, {});
}

/**
 * Localized name of a 0-based month index. The sample date is anchored to
 * UTC and formatted with an explicit UTC timezone, so the name is
 * independent of both the browser timezone and the Intl polyfill's default
 * timezone (which is UTC, not the browser's — formatting a locally
 * constructed date with it shifts the month for buyers ahead of UTC).
 */
export function getMonthName(locale: string, monthIndex: number, format: 'short' | 'long' = 'short'): string {
  return new Date(Date.UTC(2020, monthIndex, 1)).toLocaleString(locale, {
    month: format,
    timeZone: 'UTC'
  });
}
type WeekInfo = {
  firstDay: number;
};

// The app's own locales are pinned: Intl week data varies with the engine's
// CLDR version (is-IS is Monday up to CLDR 47 and Sunday from CLDR 48), which
// would give one language a different calendar on different devices.
const pinnedWeekStarts = new Map<string, Day>([['en-GB', Day.MONDAY], ['is-IS', Day.MONDAY], ['fo-FO', Day.MONDAY]]);

/**
 * The first day of the week for a locale, as a date-fns day index
 * (0 = Sunday … 6 = Saturday). The app's locales are pinned; any other
 * locale follows Intl, which numbers days 1–7 with Sunday as 7. Engines
 * without week info fall back to Monday.
 */
export function getWeekStartsOn(locale: string): Day {
  const pinned = pinnedWeekStarts.get(locale);
  if (pinned !== undefined) return pinned;
  try {
    const intlLocale = new Intl.Locale(locale) as Intl.Locale & {
      getWeekInfo?: () => WeekInfo;
      weekInfo?: WeekInfo;
    };
    const firstDay = (intlLocale.getWeekInfo?.() ?? intlLocale.weekInfo)?.firstDay;
    return typeof firstDay === 'number' ? firstDay % 7 as Day : Day.MONDAY;
  } catch {
    return Day.MONDAY;
  }
}